"use client";

import AutoScroll from "embla-carousel-auto-scroll";
import Image from "next/image";
import { useEffect, useRef } from "react";

import { css } from "@/../styled-system/css";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/Carousel";

const allImages = Array.from(
  { length: 40 },
  (_, i) => `/png_files/universe_of_style/UOSImage${i + 1}.png`,
);

const chunkImages = (arr: string[], chunkSize: number) =>
  Array.from({ length: Math.ceil(arr.length / chunkSize) }, (_, i) =>
    arr.slice(i * chunkSize, i * chunkSize + chunkSize),
  );

function UniverseOfStyle() {
  return (
    <div
      className={css({
        py: "60px",
        backgroundColor: "brandPewter",
      })}
    >
      <div className={css({ maxWidth: "1680px", mx: "auto", width: "90%" })}>
        <div
          className={css({
            mb: "60px",
            textAlign: "center",
          })}
        >
          <span
            className={css({
              color: "brandDarkBrown",
              textStyle: "subheadingMobile",
              mb: "3",
              lineHeight: "normal",
              display: "block",
              sm: {
                textStyle: "subheading1",
              },
            })}
          >
            EXPLORE STYLES AND PRODUCTS
          </span>
          <h1
            className={css({
              color: "brandBlack",
              textStyle: "headerMobile",
              sm: { textStyle: "header1" },
            })}
          >
            UNIVERSE OF STYLE
          </h1>
        </div>
        <MosaicGrid />
      </div>
    </div>
  );
}

interface MosaicRowProps {
  images: string[];
  offset?: boolean;
}

function MosaicRow({ images, offset = false }: MosaicRowProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<ReturnType<typeof AutoScroll> | null>(null);

  // Save autoscroll plugin instance
  autoScrollRef.current = AutoScroll({
    startDelay: 1000,
    stopOnMouseEnter: false,
    stopOnInteraction: false,
    direction: offset ? "backward" : "forward",
    speed: 0.5,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !autoScrollRef.current) return;

    let timeout: NodeJS.Timeout;

    const handleMouseEnter = () => {
      autoScrollRef.current?.stop();
      clearTimeout(timeout);
    };

    const handleMouseLeave = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        autoScrollRef.current?.play();
      }, 1000);
    };

    container.addEventListener("mouseenter", handleMouseEnter);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      container.removeEventListener("mouseenter", handleMouseEnter);
      container.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  const customAlign = () => (offset ? 90 : 0);

  return (
    <Carousel
      ref={containerRef}
      css={{ overflow: "hidden", w: "full" }}
      opts={{ loop: true, align: customAlign, dragFree: true }}
      plugins={[autoScrollRef.current]}
    >
      <CarouselContent
        css={{
          display: "flex",
          ml: "-1.5",
          sm: {
            ml: "-3",
          },
        }}
      >
        {[...images, ...images].map((src, idx) => (
          <CarouselItem
            key={`${src}-${idx}`}
            css={{
              pl: "1.5",
              flexBasis: "132px",
              sm: {
                pl: "3",
                flexBasis: "192px",
              },
            }}
          >
            <div
              className={css({
                overflow: "hidden",
                width: "120px",
                height: "120px",
                sm: {
                  width: "180px",
                  height: "180px",
                },
              })}
            >
              <Image
                src={src}
                alt="Mosaic Image"
                width={180}
                height={180}
                className={css({
                  objectFit: "cover",
                  transition: "transform 0.3s ease",
                  _hover: {
                    transform: "scale(1.2)",
                  },
                })}
              />
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious />
      <CarouselNext />
    </Carousel>
  );
}

function MosaicGrid() {
  const imageChunks = chunkImages(allImages, 10);
  return (
    <div
      className={css({
        display: "flex",
        flexDir: "column",
        gap: "3",
        backgroundColor: "white",
        py: "5",
        rounded: "40px",
        overflow: "clip",
      })}
    >
      {imageChunks.map((chunk, i) => (
        <MosaicRow key={i} images={chunk} offset={i % 2 === 1} />
      ))}
    </div>
  );
}

export default UniverseOfStyle;
